import { useEffect, useState } from "react"
import { useRouter } from "next/router"

import {
  FEATURE_FLAGS,
  useOrganizationFeatureFlagScope,
  usePostHydrationFeatureFlagValue,
} from "@/feature-flags"
import { useOrganization } from "@clerk/nextjs"

import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

const UPLOADED_CONVERSATIONS_VISUALIZER_FLAG_FALLBACK_MS = 2_000

/**
 * Resolves the visualizer rollout for the organization named by the URL.
 */
export function useUploadedConversationsVisualizerFeatureFlag():
  | boolean
  | undefined {
  const router = useRouter()
  const { organization } = useOrganization()
  const flagValue = usePostHydrationFeatureFlagValue(
    FEATURE_FLAGS.CONVERSATION_CLUSTER_VISUALIZER,
  )
  const { hasError, isReady } = useOrganizationFeatureFlagScope()
  const routeOrganizationSlug = router.isReady
    ? getRouteOrganizationSlug(router.asPath)
    : null
  const activeOrganizationMatchesRoute =
    routeOrganizationSlug !== null &&
    organization?.slug === routeOrganizationSlug
  const scopeKey = routeOrganizationSlug
    ? `${routeOrganizationSlug}:${organization?.id ?? "unresolved"}`
    : null
  const [timedOutScopeKey, setTimedOutScopeKey] = useState<string | null>(null)

  useEffect(() => {
    const matchingScopeFailed = activeOrganizationMatchesRoute && hasError
    const matchingScopeResolved =
      activeOrganizationMatchesRoute && isReady && flagValue !== undefined
    if (scopeKey === null || matchingScopeFailed || matchingScopeResolved) {
      return
    }

    const timeout = window.setTimeout(() => {
      setTimedOutScopeKey(scopeKey)
    }, UPLOADED_CONVERSATIONS_VISUALIZER_FLAG_FALLBACK_MS)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [activeOrganizationMatchesRoute, flagValue, hasError, isReady, scopeKey])

  if (!router.isReady || scopeKey === null) {
    return undefined
  }

  if (!activeOrganizationMatchesRoute) {
    return timedOutScopeKey === scopeKey ? false : undefined
  }

  if (hasError) {
    return false
  }

  if (!isReady || flagValue === undefined) {
    return timedOutScopeKey === scopeKey ? false : undefined
  }

  return flagValue
}
